import { forwardRef } from "react";

import type { FulfillmentOrderDetail } from "../types";
import {
  feeAdjustmentSpecUnit,
  fulfillmentItineraryLocationSummary,
  fulfillmentItineraryRouteSummary,
  fulfillmentResourceTypeLabel,
  fulfillmentServiceProviderName,
  pricingUnitTypeLabel,
} from "../service";

type Props = { detail: FulfillmentOrderDetail };

type AgreementDocumentLine = {
  key: string;
  resourceType: string;
  itemName: string;
  itemSpec: string | null | undefined;
  fulfillmentSupplierName: string | null | undefined;
  serviceDateFrom: string | null | undefined;
  serviceDateTo: string | null | undefined;
  travelerRole: string | null | undefined;
  mealType: string | null | undefined;
  pricingUnitType: string;
  quantity: string;
  billingCount: string;
  unit: string;
  unitPrice: string;
  amount: string;
  remark: string | null | undefined;
};

const fallbackText = (value: string | null | undefined) => value?.trim() || "待补充";

const formatAmount = (value: string | number | null | undefined) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return fallbackText(value == null ? null : String(value));
  return new Intl.NumberFormat("zh-CN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

const serviceDate = (line: AgreementDocumentLine) => {
  if (line.serviceDateFrom && line.serviceDateTo) {
    return line.serviceDateFrom === line.serviceDateTo
      ? line.serviceDateFrom
      : `${line.serviceDateFrom} 至 ${line.serviceDateTo}`;
  }
  return line.serviceDateFrom ?? line.serviceDateTo ?? "待确认";
};

/** 协议项目副标签去除与项目名相同的重复餐型，同时保留角色等有效信息。 */
const itemSecondaryLabels = (line: AgreementDocumentLine) => Array.from(new Set(
  [line.travelerRole, line.mealType]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value) && value !== line.itemName.trim()),
));

/** 以可打印正式文书结构展示当前确认协议。 */
export const FulfillmentOrderAgreementDocument = forwardRef<HTMLElement, Props>(
  function FulfillmentOrderAgreementDocument(props, ref) {
  const detail = props.detail;
  const confirmedAt = detail.currentVersion?.createdAt?.slice(0, 10) || "待补充";
  const companyPartyName = detail.companyPartyName;
  const companyContactName = detail.companyContactName;
  const companyContactPhone = detail.companyContactPhone;
  const companySealUrl = import.meta.env.VITE_FULFILLMENT_ORDER_COMPANY_SEAL_URL?.trim()
    || "/assets/fulfillment-order/company-seal.png";
  const settlementSupplierName = detail.order.settlementSupplierName;
  const supplierContactName = detail.order.supplierContactName;
  const supplierContactPhone = detail.order.supplierContactPhone;
  const amount = detail.currentVersion?.amount ?? detail.order.currentAmount;
  const lines: AgreementDocumentLine[] = detail.lines.map((line) => ({
    key: line.id,
    resourceType: line.resourceType,
    itemName: line.itemNameSnapshot,
    itemSpec: line.itemSpecSnapshot,
    fulfillmentSupplierName: fulfillmentServiceProviderName(line),
    serviceDateFrom: line.serviceDateFrom,
    serviceDateTo: line.serviceDateTo,
    travelerRole: line.travelerRole,
    mealType: line.mealType,
    pricingUnitType: line.pricingUnitType,
    quantity: line.quantity,
    billingCount: line.billingCount,
    unit: line.unit,
    unitPrice: line.unitPriceAmount,
    amount: line.amount,
    remark: line.remark,
  }));
  const itineraryDays = detail.itineraryDays ?? [];

  return (
    <article
      ref={ref}
      className="fulfillment-order-agreement-document"
      aria-label="花儿艺术旅行出行确认件"
    >
      <header className="fulfillment-order-agreement-document-header">
        <h2>花儿艺术旅行出行确认件</h2>
      </header>

      <section className="fulfillment-order-agreement-document-parties" aria-label="签约主体">
        <div>
          <span>甲方</span>
          <strong>{fallbackText(companyPartyName)}</strong>
          <small>联系人：{fallbackText(companyContactName)}</small>
          <small>联系方式：{fallbackText(companyContactPhone)}</small>
        </div>
        <div>
          <span>乙方</span>
          <strong>{fallbackText(settlementSupplierName)}</strong>
          <small>联系人：{fallbackText(supplierContactName)}</small>
          <small>联系方式：{fallbackText(supplierContactPhone)}</small>
          <small>确认日期：{confirmedAt}</small>
        </div>
      </section>

      <p className="fulfillment-order-agreement-document-warning">
        请核对项目内容、价格与履约日期，确认无误后下载盖章。
      </p>

      <section className="fulfillment-order-agreement-document-standards" aria-labelledby="agreement-standards-title">
        <h3 id="agreement-standards-title">接待标准</h3>
        <div className="fulfillment-order-agreement-document-table-scroll">
          <table aria-label="接待标准">
            <thead>
              <tr>
                <th scope="col">类型</th>
                <th scope="col">服务项目</th>
                <th scope="col">规格/服务供应商</th>
                <th scope="col">服务日期</th>
                <th scope="col">数量与计价</th>
                <th scope="col">金额</th>
                <th scope="col">备注</th>
              </tr>
            </thead>
            <tbody>
              {lines.length > 0 ? lines.map((line) => {
                const secondaryLabels = itemSecondaryLabels(line);
                return (
                <tr key={line.key}>
                  <td>{fulfillmentResourceTypeLabel(line.resourceType)}</td>
                  <td>
                    <strong>{line.itemName}</strong>
                    {secondaryLabels.length > 0 ? (
                      <small>
                        {secondaryLabels.join(" / ")}
                      </small>
                    ) : null}
                  </td>
                  <td>
                    <span>{line.itemSpec || "-"}</span>
                    <small>{line.fulfillmentSupplierName || "服务供应商待确认"}</small>
                  </td>
                  <td>{serviceDate(line)}</td>
                  <td>
                    <span>{line.quantity} {line.unit} × {line.billingCount} {feeAdjustmentSpecUnit(line.resourceType)}</span>
                    <small>{pricingUnitTypeLabel(line.pricingUnitType)} / 单价 ¥ {formatAmount(line.unitPrice)}</small>
                  </td>
                  <td className="money-value">¥ {formatAmount(line.amount)}</td>
                  <td>{line.remark || "-"}</td>
                </tr>
                );
              }) : (
                <tr>
                  <td colSpan={7} className="fulfillment-order-agreement-document-empty">
                    暂无接待标准
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={5}>总计：</th>
                <td className="money-value">
                  ¥ {formatAmount(amount)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section
        className="fulfillment-order-agreement-document-itinerary"
        aria-labelledby="agreement-itinerary-title"
      >
        <h3 id="agreement-itinerary-title">行程安排</h3>
        {itineraryDays.length > 0 ? (
          <div className="fulfillment-order-agreement-itinerary-list">
            {itineraryDays.map((day) => (
              <div
                className="fulfillment-order-agreement-itinerary-day"
                key={`${day.dayNo}-${day.date ?? "pending"}`}
              >
                <div>
                  <strong>{day.date || `第 ${day.dayNo} 天`}</strong>
                  <span>{fulfillmentItineraryLocationSummary(day.locationSummary)}</span>
                  <span>住宿：{day.stayName || "待确认"}</span>
                </div>
                <p>{fulfillmentItineraryRouteSummary(day.routeSummary)}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="fulfillment-order-agreement-itinerary-empty">暂无行程安排</p>
        )}
      </section>

      <section className="fulfillment-order-agreement-document-signatures" aria-label="签约盖章">
        <div className="fulfillment-order-agreement-document-signature--company">
          <strong>甲方签约（盖章）：</strong>
          {companySealUrl ? (
            <img
              className="fulfillment-order-agreement-company-seal"
              src={companySealUrl}
              alt={`${fallbackText(companyPartyName)}公章`}
              crossOrigin="anonymous"
              data-company-seal="true"
            />
          ) : null}
          <span>签约时间：{confirmedAt}</span>
        </div>
        <div>
          <strong>乙方签约（盖章）：</strong>
          <span>签约时间：{confirmedAt}</span>
        </div>
      </section>
    </article>
  );
});
